import api from '@/services/api'
import { camelToSnake } from '../../../utils/object'

const BATCH_SIZE = 15
export default class EmailTemplateAPI {
  static TEMPLATES_ENDPOINT = 'email-templates'
  static BULK_EMAIL_TEMPLATE_ENDPOINT = 'email-templates/send-email-template/'

  /**
   * Initialize a `EmailTemplateAPI` instance.
   *
   * @param {class} cls  The class to use to create EmailTemplate objects.
   **/
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `EmailTemplateAPI`.
   *
   * @param {class} cls      The class to use to create EmailTemplate objects.
   **/
  static create(cls) {
    return new EmailTemplateAPI(cls)
  }

  /**
   * Get the API Client.
   */
  get client() {
    return api.getApiClient()
  }

  /**
   * Retrieve one EmailTemplate from the API.
   *
   * @param {String} templateID  Unique ID of the EmailTemplate to retrieve.
   */
  get(templateID) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/${templateID}/`
    return this.client.get(url).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Create a EmailTemplate via the API.
   */
  create(template, fields = []) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/`
    const data = this.cls.toAPI(template, fields)
    return this.client
      .post(url, data)
      .then(response => response.data)
      .then(this.cls.fromAPI)
  }

  /**
   * Get all EmailTemplates through the API.
   *
   *
   */
  list({ filters = {} } = {}) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/`
    const options = {
      params: {},
    }

    // Filters

    if (filters.pageSize) {
      options.params.page_size = filters.pageSize
    }

    if (filters.page) {
      options.params.page = filters.page
    }

    return this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => {
        data.results = data.results.map(this.cls.fromAPI)
        return data
      })
  }

  /**
   * Update a EmailTemplate through the API.
   *
   * @param {template} template  EmailTemplate data to send to the API.
   */
  update(template, fields = [], excludeFields = []) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/${template.id}/`
    const data = this.cls.toAPI(template, fields, excludeFields)
    return this.client.patch(url, data).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Register a new EmailTemplate through the API.
   *
   * @param {Object} data   Object with the fields required for registration.
   */
  register(data) {
    //console.log("register")
    return this.client
      .post(`${EmailTemplateAPI.TEMPLATES_ENDPOINT}/`, camelToSnake(data))
      .then(response => this.cls.fromAPI(response.data))
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Register EmailTemplate API error')
        throw error
      })
  }

  /**
   * Bulk email a set of users from a specific EmailTemplate
   *
   * @param {String} templateID       ID of the EmailTemplate to send
   * @param {Array<String>} userIds   Array of User IDS to email
   */
  async bulkEmailTemplate(templateId, userIds) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/${templateId}/bulk-email/`
    const data = {
      template_id: templateId,
    }
    let start = 0
    let end, result
    /**
     * Emails are batched into groups of BATCH_SIZE
     * App is currently hosted on Heroku in production which will time out any requests that have been going on for 30 seconds.
     * Splitting this into multiple small requests avoids this issue.
     */
    while (start < userIds.length) {
      end = start + BATCH_SIZE
      data.user_ids = userIds.slice(start, end)
      result = await this.client.post(url, data)
      result = result.data
      start = end
    }
    return result
  }

  /**
   * Preview an email from an EmailTemplate for a specific user.
   *
   * @param {String} templateID       ID of the EmailTemplate to send
   * @param {String} userId           ID of the user to preview the email for
   */
  preview(templateId, userId) {
    const url = `${EmailTemplateAPI.TEMPLATES_ENDPOINT}/${templateId}/preview-email/`
    const data = {
      user_id: userId,
    }

    return this.client.post(url, data).then(response => {
      return response.data
    })
  }
}
